import { yupResolver } from '@hookform/resolvers/yup';
import { Check, Close } from '@mui/icons-material';
import { Box, Stack, styled } from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { getOidcContext } from '@/apis/experience';

import { ERROR_MESSAGES, SIGN_UP_URL } from '../../constants/index.js';
import { RegistrationStep } from '../../constants/steps.js';
import { useRegistration } from '../../contexts/RegistrationContext.js';
import { useCustomRegistrationConfig } from '../../hooks/use-custom-registration-config.js';
import { fetchData } from '../../utils/fetch-data.js';
import { getFieldErrorMessage } from '../../utils/get-field-error-message.js';
import {
  evaluatePasswordRules,
  PASSWORD_CHECKLIST_ITEMS,
} from '../../validation/password-rules.js';
import { createPasswordSchema, type PasswordFormData } from '../../validation/schemas.js';
import { ErrorText, ListText, PasswordField, PrimaryButton } from '../shared/index.js';

type SignUpResponse = {
  readonly details?: { readonly otpExpiresAt?: string };
  readonly detail?: string;
};

const StyledList = styled('ul')(({ theme }) => ({
  color: theme.palette.text.primary,
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
}));

const StyledListItem = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Password = () => {
  const {
    accessInfo,
    completeStep,
    error,
    isLoading,
    password,
    setCurrentStep,
    setError,
    setIsLoading,
    setOtpExpiresAt,
    setPassword,
    userInfo,
  } = useRegistration();

  const config = useCustomRegistrationConfig();
  const passwordSchemaWithContext = createPasswordSchema(userInfo);

  const {
    control,
    handleSubmit,
    setError: setFormError,
    formState: { dirtyFields, errors, touchedFields },
  } = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchemaWithContext),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      confirmPassword: password.confirmValue,
      password: password.value,
    },
  });
  const passwordValue = useWatch({ control, name: 'password' });
  const passwordRules = evaluatePasswordRules(passwordValue, userInfo);
  const isPasswordTouched = [touchedFields.password, dirtyFields.password].some(Boolean);

  const handleCompleteStep = (data: PasswordFormData) => {
    setPassword({ confirmValue: data.confirmPassword, value: data.password });
    completeStep(RegistrationStep.PASSWORD);
    setCurrentStep(RegistrationStep.CONFIRMATION_CODE);
  };

  const onSubmit = async (data: PasswordFormData) => {
    setError(undefined);
    setIsLoading(true);

    if (!config?.apiUrl) {
      setError('Registration service is not configured.');
      setIsLoading(false);
      return;
    }

    try {
      const apiBaseUrl = config.apiUrl;
      const { clientId } = await getOidcContext();

      const response = await fetchData(`${apiBaseUrl}${SIGN_UP_URL}`, 'POST', {
        body: JSON.stringify({ ...userInfo, ...accessInfo, clientId, password: data.password }),
      });

      if (response.status === 200) {
        handleCompleteStep(data);
        setOtpExpiresAt(undefined);
        return;
      }

      const responseData: SignUpResponse = await response.json();

      if (responseData.details?.otpExpiresAt) {
        handleCompleteStep(data);
        setOtpExpiresAt(responseData.details.otpExpiresAt);
        return;
      }

      const passwordError = getFieldErrorMessage(responseData, 'password');

      if (passwordError) {
        setFormError('password', { type: 'server', message: passwordError });
        return;
      }

      throw new Error(responseData.detail ?? ERROR_MESSAGES.REGISTRATION_FAILED);
    } catch (apiError) {
      setError(apiError instanceof Error ? apiError.message : ERROR_MESSAGES.REGISTRATION_FAILED);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
        <ListText>Your password must meet the following requirements:</ListText>
        <StyledList>
          {PASSWORD_CHECKLIST_ITEMS.map((rule) => {
            const isRuleMet = isPasswordTouched && passwordRules[rule.id];
            const checklistColor = isRuleMet
              ? 'success.main'
              : isPasswordTouched
                ? 'error.main'
                : 'text.secondary';
            const RuleIcon = isRuleMet ? Check : Close;

            return (
              <StyledListItem key={rule.id}>
                <RuleIcon fontSize="small" sx={{ color: checklistColor }} />
                <ListText sx={{ color: checklistColor }}>{rule.label}</ListText>
              </StyledListItem>
            );
          })}
        </StyledList>
      </Box>

      {errors.confirmPassword && (
        <Box sx={{ mb: 2 }}>
          <ErrorText sx={{ fontSize: 16 }}>{errors.confirmPassword.message}</ErrorText>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <ErrorText sx={{ fontSize: 16 }}>{error}</ErrorText>
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <PasswordField
                {...field}
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
                label="Password"
                placeholder="Password"
              />
            )}
          />
          <Controller
            name="confirmPassword"
            control={control}
            render={({ field }) => (
              <PasswordField
                {...field}
                error={Boolean(errors.confirmPassword)}
                label="Confirm Password"
                placeholder="Confirm Password"
              />
            )}
          />
        </Stack>

        <PrimaryButton disabled={isLoading} isLoading={isLoading} type="submit">
          {isLoading ? 'Registering...' : 'Register'}
        </PrimaryButton>
      </form>
    </Box>
  );
};

export default Password;
